import { createFileRoute, useParams } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Copy, Eye, EyeOff, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import { formatNumber, truncateApiKey } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/api-keys/$keyId")({
  component: ApiKeyDetail,
});

function ApiKeyDetail() {
  const { keyId } = useParams({ from: "/dashboard/api-keys/$keyId" });
  const [apiKey, setApiKey] = useState<any>(null);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const response = await apiClient.getApiKey(keyId);
        setApiKey(response.apiKey);
      } catch (error) {
        console.error("Failed to fetch API key:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApiKey();
  }, [keyId]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDelete = async () => {
    if (
      !confirm(
        "Are you sure you want to delete this API key? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await apiClient.deleteApiKey(keyId);
      // Navigate back to API keys list
    } catch (error) {
      console.error("Failed to delete API key:", error);
    }
  };

  if (loading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </ProtectedRoute>
    );
  }

  if (!apiKey) {
    return (
      <ProtectedRoute>
        <DashboardLayout>
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-900">
              API Key Not Found
            </h2>
            <p className="text-gray-600 mt-2">
              The requested API key could not be found.
            </p>
          </div>
        </DashboardLayout>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="space-y-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {apiKey.name}
              </h1>
              <p className="text-gray-600">
                Created {new Date(apiKey.created_at).toLocaleDateString()}
              </p>
            </div>
            {/* <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Key
            </Button> */}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>API Key Details</CardTitle>
              <CardDescription>
                Use this key to authenticate your API requests
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono">
                  {showKey ? apiKey.unkey : truncateApiKey(apiKey.unkey)}
                </code>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowKey(!showKey)}
                  >
                    {showKey ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(apiKey.unkey)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Usage Count
                  </Label>
                  <p className="text-lg font-semibold">
                    {formatNumber(apiKey.usage_count)}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Last Used
                  </Label>
                  <p className="text-lg font-semibold">
                    {apiKey.last_used_at
                      ? new Date(apiKey.last_used_at).toLocaleDateString()
                      : "Never"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ProtectedRoute>
  );
}
